import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import type { TeamFlagContext } from "@redotech/feature-flags-model/context";
import type { FeatureFlag } from "@redotech/openfeature-contracts/flag";
import { useRequiredContext } from "@redotech/react-util/context";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { RedoPlatformAuthRpcClientContext } from "./platform/redo-platform-rpc-client-provider";
import { TeamContext } from "./team";
import { UserContext } from "./user";

export interface TeamFeatureFlagOptions {
  /** Set to false when Redo users need to preview the team's actual flag value. */
  readonly forceEnabledForRedoUsers?: boolean;
}

export interface TeamFeatureFlagState {
  readonly pending: boolean;
  readonly enabled: boolean;
}

export function useTeamFeatureFlag(
  flag: FeatureFlag<TeamFlagContext, boolean>,
  { forceEnabledForRedoUsers = true }: TeamFeatureFlagOptions = {},
): TeamFeatureFlagState {
  const user = useContext(UserContext);
  const team = useRequiredContext(TeamContext);
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );

  const forceEnabled =
    forceEnabledForRedoUsers && isRedoEmailAddress(user?.email);

  const query = useQuery({
    queryKey: ["featureFlag", flag.key, String(team._id)],
    queryFn: async () => {
      const response = await platformRpcClient.getFeatureFlag({
        flagKey: flag.key,
        defaultValue: flag.defaultValue,
      });
      return response.enabled;
    },
    enabled: !forceEnabled,
    staleTime: Infinity,
  });

  return {
    pending: !forceEnabled && query.isPending,
    enabled: forceEnabled || (query.data ?? false),
  };
}
